import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Button,
    FormControl,
    Grid,
    InputLabel,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';
import UserForm from './UserForm';
import { connect } from 'react-redux';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import RentalService from '../../services/rental.service';
import SnackbarNotify from '../../utils/snackbar/SnackbarNotify';
import { useNavigate } from 'react-router';
import {
    RentalStatusEnum,
    UPDATE_RENTAL_ALLOWED_STATUSES,
    UPDATE_RENTAL_BLOCKED_STATUSES
} from '../../utils/constants/rental-status';
import { useAuth } from '../../contexts/AuthContext';
import { Loader } from '../index';
import { useTheme } from '@mui/material/styles';
import {
    formatLocalDateToUtcTime,
    parseUtcTimeToLocalDate
} from '../../utils/helpers/date-time.helper';

export const normalizeText = value =>
    value?.trim().replace(/\s+/g, ' ').toLowerCase() || null;

const phoneNumberSchema = () =>
    Yup.string()
        .required('Phone is required')
        .test('is-valid-phone', 'Phone number needs to be valid', function (value) {
            const { country, code } = this.parent;
            if (!value) return false;
            try {
                return Yup.string().phone(country).isValidSync(`${code}${value}`);
            } catch {
                return false;
            }
        });

const RentalForm = ({ orgProperties, activeOrg, selectedRental, isEditMode = false }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [propertyOptions, setPropertyOptions] = useState([]);
    const [bookedRanges, setBookedRanges] = useState([]);
    const [removedGuests, setRemovedGuests] = useState([]);

    const searchUser = async (fieldPrefix, values, setFieldValue) => {
        try {
            const phone = values.phone.number
                ? `${values.phone.code}${values.phone.number}`
                : null;
            const email = values.email || null;

            const phoneOrEmail = phone || email;

            const res = await RentalService.findUser(phoneOrEmail.toLowerCase());

            if (res) {
                setFieldValue(`${fieldPrefix}.first_name`, res.first_name);
                setFieldValue(`${fieldPrefix}.last_name`, res.last_name);
                setFieldValue(`${fieldPrefix}.email`, res.email);
                setFieldValue(`${fieldPrefix}.phone.number`, res.phone?.number || '');
                setFieldValue(`${fieldPrefix}.phone.code`, res.phone?.code || '1');
                setFieldValue(`${fieldPrefix}.phone.country`, res.phone?.country || 'US');
                setFieldValue(`${fieldPrefix}.renterFound`, true);
            } else {
                setFieldValue(`${fieldPrefix}.renterFound`, false);
            }
        } catch (e) {
            setFieldValue(`${fieldPrefix}.renterFound`, false);
        }
    };

    const getDefaultTime = (hours, minutes = 0) => {
        const d = new Date();
        d.setHours(hours, minutes, 0, 0);
        return d;
    };

    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const res = await RentalService.getRentalOrgProperties(
                    user?.active_org_id
                );
                setPropertyOptions(res);
            } catch (err) {
                console.error('Error fetching properties:', err);
            }
        };

        if (user?.active_org_id) {
            fetchProperties();
        }
    }, [user?.active_org_id]);

    const initialValues = React.useMemo(() => {
        if (!selectedRental)
            return {
                property_id: '',
                check_in_date: null,
                check_in_time:
                    parseUtcTimeToLocalDate(activeOrg?.check_in_time) ||
                    getDefaultTime(12, 0),
                check_out_date: null,
                check_out_time:
                    parseUtcTimeToLocalDate(activeOrg?.check_out_time) ||
                    getDefaultTime(10, 0),
                renter: {
                    phone: { number: '', code: 1, country: 'US' },
                    first_name: '',
                    last_name: '',
                    email: '',
                    renterFound: undefined
                },
                guests: []
            };
        return {
            property_id: selectedRental.property_id,
            check_in_date: new Date(selectedRental.start_date._seconds * 1000),
            check_in_time: new Date(selectedRental.start_date._seconds * 1000),
            check_out_date: new Date(selectedRental.end_date._seconds * 1000),
            check_out_time: new Date(selectedRental.end_date._seconds * 1000),
            renter: {
                phone: {
                    number: selectedRental.renter?.phone?.number || '',
                    code: selectedRental.renter?.phone?.code || 1,
                    country: selectedRental.renter?.phone?.country || 'US'
                },
                first_name: selectedRental.renter?.first_name || '',
                last_name: selectedRental.renter?.last_name || '',
                email: selectedRental.renter?.email || '',
                renterFound: Boolean(selectedRental.renter?.id)
            },
            guests:
                selectedRental.guests?.map(g => ({
                    id: g.id,
                    phone: {
                        number: g.phone.number,
                        code: g.phone.code,
                        country: g.phone.country
                    },
                    first_name: g.first_name,
                    last_name: g.last_name,
                    email: g.email,
                    minor: g.minor || false,
                    renterFound: g.source === 'user' || g.source === 'invite'
                })) || []
        };
    }, [selectedRental]);

    const validationSchema = Yup.object().shape({
        property_id: Yup.string().required('Property is required'),
        check_in_date: Yup.string().required('Check-in date is required'),
        check_out_date: Yup.string().required('Check-out date is required'),
        check_in_time: Yup.string().required('Check-in time is required'),
        check_out_time: Yup.string().required('Check-out time is required'),
        renter: Yup.object().shape({
            phone: Yup.object().shape({
                number: phoneNumberSchema(),
                code: Yup.number(),
                country: Yup.string()
            }),
            first_name: Yup.string().required('First name required'),
            last_name: Yup.string().required('Last name required'),
            email: Yup.string().email('Invalid email').optional()
        }),
        guests: Yup.array().of(
            Yup.object().shape({
                phone: Yup.object().shape({
                    number: phoneNumberSchema(),
                    code: Yup.string(),
                    country: Yup.string()
                }),
                first_name: Yup.string().required('First name required'),
                last_name: Yup.string().required('Last name required'),
                email: Yup.string().email('Invalid email').nullable().optional()
            })
        )
    });

    const getFormattedDateTime = (date, time) => {
        const dt = new Date(date);

        const utcTime = formatLocalDateToUtcTime(time); // "HH:mm"
        const [hours, minutes] = utcTime.split(':').map(Number);

        dt.setHours(hours, minutes, 0, 0);

        const year = dt.getFullYear();
        const month = String(dt.getMonth() + 1).padStart(2, '0');
        const day = String(dt.getDate()).padStart(2, '0');
        const h = String(dt.getHours()).padStart(2, '0');
        const m = String(dt.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day} ${h}:${m}`;
    };

    const handleSubmit = async values => {
        const formattedData = {
            property_id: values.property_id,
            org_id: activeOrg.org_id,
            start_date: getFormattedDateTime(values.check_in_date, values.check_in_time),
            end_date: getFormattedDateTime(values.check_out_date, values.check_out_time),
            renter: {
                phone: {
                    number: values.renter.phone.number,
                    code: Number(values.renter.phone.code),
                    country: values.renter.phone.country
                },
                first_name: normalizeText(values.renter.first_name),
                last_name: normalizeText(values.renter.last_name),
                email: normalizeText(values.renter.email)
            },
            guests: values.guests.map(guest => ({
                phone: {
                    number: guest.phone.number,
                    code: Number(guest.phone.code),
                    country: guest.phone.country
                },
                first_name: normalizeText(guest.first_name),
                last_name: normalizeText(guest.last_name),
                email: normalizeText(guest.email),
                minor: guest.minor
            }))
        };
        const updatedData = {
            start_date: getFormattedDateTime(values.check_in_date, values.check_in_time),

            end_date: getFormattedDateTime(values.check_out_date, values.check_out_time),

            added_guests: values.guests
                .filter(g => !g.id)
                .map(guest => ({
                    phone: {
                        number: guest.phone.number,
                        code: Number(guest.phone.code),
                        country: guest.phone.country
                    },
                    first_name: normalizeText(guest.first_name),
                    last_name: normalizeText(guest.last_name),
                    email: normalizeText(guest.email),
                    minor: guest.minor
                })),
            removed_guest_ids: removedGuests
        };
        try {
            setLoading(true);
            if (isEditMode) {
                await RentalService.updateRental(
                    activeOrg.org_id,
                    selectedRental.rental_id,
                    updatedData
                );
                SnackbarNotify.success('Reservation updated successfully!');
            } else {
                await RentalService.createRental(activeOrg.org_id, formattedData);
                SnackbarNotify.success(
                    'Reservation created successfully and invite sent!'
                );
            }

            navigate('/rentals');
        } catch (e) {
            SnackbarNotify.error('Failed: ' + (e.message || 'Something went wrong'));
        } finally {
            setLoading(false);
        }
    };

    const canEditRental =
        isEditMode &&
        selectedRental &&
        UPDATE_RENTAL_ALLOWED_STATUSES.includes(selectedRental.status);

    const disabled = selectedRental
        ? UPDATE_RENTAL_BLOCKED_STATUSES.includes(selectedRental.status) || loading
        : false;

    const fetchBookedDates = async propertyId => {
        try {
            const res = await RentalService.getRentalsByPropertyId(
                activeOrg.org_id,
                propertyId
            );

            const ranges = (res || [])
                .filter(r => r.rental_id !== selectedRental?.rental_id)
                .map(r => ({
                    start: new Date(r.start_date._seconds * 1000),
                    end: new Date(r.end_date._seconds * 1000),
                    status: r.status
                }));

            setBookedRanges(ranges);
        } catch (err) {
            console.error('Error fetching rentals:', err);
        }
    };

    // expired/suspended reservations no longer hold the property and
    // should not block extending an active reservation's end date
    const CHECKOUT_NON_BLOCKING_STATUSES = [
        RentalStatusEnum.EXPIRED,
        RentalStatusEnum.SUSPENDED
    ];

    const isDateValidForCheckout = date => {
        return bookedRanges
            .filter(range => !CHECKOUT_NON_BLOCKING_STATUSES.includes(range.status))
            .some(range => {
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);

                const start = new Date(range.start);
                start.setHours(0, 0, 0, 0);

                const end = new Date(range.end);
                end.setHours(0, 0, 0, 0);

                return d > start && d < end;
            });
    };

    const isDateValidForCheckIn = date => {
        return bookedRanges.some(range => {
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);

            const start = new Date(range.start);
            start.setHours(0, 0, 0, 0);

            const end = new Date(range.end);
            end.setHours(0, 0, 0, 0);

            return d >= start && d < end;
        });
    };

    const getMinCheckinTime = checkInDate => {
        if (!checkInDate) return null;

        const selected = new Date(checkInDate);
        selected.setHours(0, 0, 0, 0);

        const prevRental = bookedRanges.find(range => {
            const end = new Date(range.end);
            end.setHours(0, 0, 0, 0);
            return end.getTime() === selected.getTime();
        });

        if (!prevRental) return null;

        const min = new Date(prevRental.end);
        min.setHours(min.getHours() + 2);

        return min;
    };

    const getMaxCheckoutTime = checkOutDate => {
        if (!checkOutDate) return null;

        const selected = new Date(checkOutDate);
        selected.setHours(0, 0, 0, 0);

        const nextRental = bookedRanges.find(range => {
            const start = new Date(range.start);
            start.setHours(0, 0, 0, 0);
            return start.getTime() === selected.getTime();
        });

        if (!nextRental) return null;

        const max = new Date(nextRental.start);
        max.setHours(max.getHours() - 2);

        return max;
    };

    useEffect(() => {
        if (selectedRental?.property_id) {
            fetchBookedDates(selectedRental.property_id);
        }
        setRemovedGuests([]);
    }, [selectedRental]);

    const getFirstBlockedDateAfter = startDate => {
        if (!startDate) return null;

        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);

        const futureRanges = bookedRanges
            .filter(r => !CHECKOUT_NON_BLOCKING_STATUSES.includes(r.status))
            .map(r => new Date(r.start))
            .filter(d => d > start)
            .sort((a, b) => a - b);

        return futureRanges.length ? futureRanges[0] : null;
    };
    const handleCancel = async () => {
        navigate('/rentals');
    };

    return (
        <>
            {loading && <Loader />}
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    handleBlur,
                    handleChange,
                    setFieldValue,
                    handleSubmit,
                    errors,
                    touched
                }) => {
                    const firstBlockedDate = getFirstBlockedDateAfter(
                        values.check_in_date
                    );
                    const minCheckinTime = getMinCheckinTime(values.check_in_date);
                    const maxCheckoutTime = getMaxCheckoutTime(values.check_out_date);

                    return (
                        <form
                            onSubmit={handleSubmit}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '68vh'
                            }}
                        >
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <div
                                    style={{
                                        flex: '0 0 auto',
                                        position: 'sticky',
                                        top: 0,
                                        background: '#fff',
                                        zIndex: 1,
                                        paddingBottom: 20,
                                        borderBottom: `1px solid ${theme.palette.divider}`
                                    }}
                                >
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={4}>
                                            <Stack spacing={0.5}>
                                                <InputLabel>Address</InputLabel>
                                                <Autocomplete
                                                    id="address"
                                                    name="address"
                                                    options={Object.values(
                                                        propertyOptions || {}
                                                    )}
                                                    getOptionLabel={option =>
                                                        option.full_address ||
                                                        option.address ||
                                                        ''
                                                    }
                                                    isOptionEqualToValue={(
                                                        option,
                                                        value
                                                    ) => option.id === value?.id}
                                                    disabled={isEditMode || loading}
                                                    onChange={(_, newValue) => {
                                                        if (newValue) {
                                                            handleChange({
                                                                target: {
                                                                    name: 'property_id',
                                                                    value: newValue.id
                                                                }
                                                            });
                                                            fetchBookedDates(newValue.id);
                                                        }
                                                    }}
                                                    value={
                                                        (orgProperties || {})[
                                                            values?.property_id
                                                        ] || null
                                                    }
                                                    onBlur={handleBlur}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            required
                                                            placeholder="1234 Main St"
                                                            fullWidth
                                                            autoComplete="shipping address-line1"
                                                            error={
                                                                touched.address &&
                                                                !!errors.address
                                                            }
                                                            helperText={
                                                                touched.address &&
                                                                errors.address
                                                            }
                                                            value={values.address}
                                                            disabled={loading}
                                                        />
                                                    )}
                                                />
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Stack spacing={0.5}>
                                                <InputLabel>Check-in Date</InputLabel>
                                                <FormControl sx={{ width: '100%' }}>
                                                    <DatePicker
                                                        error={Boolean(
                                                            touched.check_in_date &&
                                                                errors.check_in_date
                                                        )}
                                                        value={values.check_in_date}
                                                        onChange={newValue =>
                                                            setFieldValue(
                                                                'check_in_date',
                                                                newValue
                                                            )
                                                        }
                                                        shouldDisableDate={
                                                            isDateValidForCheckIn
                                                        }
                                                        disabled={
                                                            isEditMode
                                                                ? disabled ||
                                                                  selectedRental.status ===
                                                                      RentalStatusEnum.ACTIVE
                                                                : false
                                                        }
                                                        minDate={new Date()}
                                                        slotProps={{
                                                            textField: {
                                                                error:
                                                                    touched.check_in_date &&
                                                                    !!errors.check_in_date,
                                                                helperText:
                                                                    touched.check_in_date &&
                                                                    errors.check_in_date
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Stack spacing={0.5}>
                                                <InputLabel>Check-in Time</InputLabel>
                                                <FormControl sx={{ width: '100%' }}>
                                                    <TimePicker
                                                        value={values.check_in_time}
                                                        onChange={newValue =>
                                                            setFieldValue(
                                                                'check_in_time',
                                                                newValue
                                                            )
                                                        }
                                                        minTime={minCheckinTime}
                                                        disabled={
                                                            isEditMode
                                                                ? disabled ||
                                                                  selectedRental.status ===
                                                                      RentalStatusEnum.ACTIVE
                                                                : false
                                                        }
                                                    />
                                                </FormControl>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Stack spacing={0.5}>
                                                <InputLabel>Check-out Date</InputLabel>
                                                <FormControl
                                                    sx={{
                                                        width: '100%',
                                                        borderColor: 'red'
                                                    }}
                                                >
                                                    <DatePicker
                                                        value={values.check_out_date}
                                                        onChange={newValue =>
                                                            setFieldValue(
                                                                'check_out_date',
                                                                newValue
                                                            )
                                                        }
                                                        shouldDisableDate={
                                                            isDateValidForCheckout
                                                        }
                                                        disabled={
                                                            isEditMode ? disabled : false
                                                        }
                                                        minDate={
                                                            values?.check_in_date
                                                                ? new Date(
                                                                      values.check_in_date.getTime()
                                                                  )
                                                                : new Date()
                                                        }
                                                        maxDate={
                                                            firstBlockedDate
                                                                ? new Date(
                                                                      firstBlockedDate.getTime()
                                                                  )
                                                                : undefined
                                                        }
                                                        slotProps={{
                                                            textField: {
                                                                error:
                                                                    touched.check_out_date &&
                                                                    !!errors.check_out_date,
                                                                helperText:
                                                                    touched.check_out_date &&
                                                                    errors.check_out_date
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Stack spacing={0.5}>
                                                <InputLabel>Check-out Time</InputLabel>
                                                <FormControl sx={{ width: '100%' }}>
                                                    <TimePicker
                                                        value={values.check_out_time}
                                                        onChange={newValue =>
                                                            setFieldValue(
                                                                'check_out_time',
                                                                newValue
                                                            )
                                                        }
                                                        maxTime={maxCheckoutTime}
                                                        disabled={
                                                            isEditMode ? disabled : false
                                                        }
                                                    />
                                                </FormControl>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </div>
                                <div
                                    style={{
                                        flex: '1 1 auto',
                                        overflowY: 'auto',
                                        paddingTop: 16,
                                        paddingBottom: 16
                                    }}
                                >
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Typography variant="h5" gutterBottom>
                                                Renter
                                            </Typography>
                                            <UserForm
                                                user={values.renter}
                                                fieldPrefix="renter"
                                                setFieldValue={setFieldValue}
                                                errors={errors.renter}
                                                touched={touched.renter}
                                                loading={loading}
                                                userFound={values.renter.renterFound}
                                                selectedRental={selectedRental}
                                                onSearch={() =>
                                                    searchUser(
                                                        'renter',
                                                        values.renter,
                                                        setFieldValue
                                                    )
                                                }
                                                disabled={disabled}
                                                isEditMode={isEditMode}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h5" gutterBottom>
                                                Guests
                                            </Typography>
                                            <FieldArray name="guests">
                                                {({ push, remove }) => (
                                                    <>
                                                        {values.guests.length === 0 && (
                                                            <Typography
                                                                variant="body2"
                                                                color="textSecondary"
                                                                sx={{
                                                                    mb: 2,
                                                                    fontStyle: 'italic'
                                                                }}
                                                            >
                                                                No Guests added
                                                            </Typography>
                                                        )}
                                                        {values.guests.map(
                                                            (guest, index) => (
                                                                <Grid
                                                                    key={index}
                                                                    container
                                                                    spacing={1}
                                                                    sx={{ mb: 2 }}
                                                                >
                                                                    <Grid item xs={12}>
                                                                        <Stack
                                                                            direction="row"
                                                                            justifyContent="space-between"
                                                                            alignItems="center"
                                                                        >
                                                                            <Typography
                                                                                variant="subtitle1"
                                                                                fontWeight="bold"
                                                                            >
                                                                                Guest{' '}
                                                                                {index +
                                                                                    1}
                                                                            </Typography>

                                                                            {(!isEditMode ||
                                                                                canEditRental) && (
                                                                                <Button
                                                                                    variant="outlined"
                                                                                    color="error"
                                                                                    onClick={() => {
                                                                                        const guest =
                                                                                            values
                                                                                                .guests[
                                                                                                index
                                                                                            ];

                                                                                        if (
                                                                                            guest.id
                                                                                        ) {
                                                                                            setRemovedGuests(
                                                                                                prev => [
                                                                                                    ...prev,
                                                                                                    guest.id
                                                                                                ]
                                                                                            );
                                                                                        }

                                                                                        remove(
                                                                                            index
                                                                                        );
                                                                                    }}
                                                                                    disabled={
                                                                                        loading
                                                                                    }
                                                                                >
                                                                                    Remove
                                                                                </Button>
                                                                            )}
                                                                        </Stack>
                                                                    </Grid>

                                                                    <Grid item xs={12}>
                                                                        <UserForm
                                                                            user={guest}
                                                                            fieldPrefix={`guests.${index}`}
                                                                            loading={
                                                                                loading
                                                                            }
                                                                            setFieldValue={
                                                                                setFieldValue
                                                                            }
                                                                            errors={
                                                                                errors
                                                                                    .guests?.[
                                                                                    index
                                                                                ]
                                                                            }
                                                                            touched={
                                                                                touched
                                                                                    .guests?.[
                                                                                    index
                                                                                ]
                                                                            }
                                                                            userFound={
                                                                                guest.renterFound
                                                                            }
                                                                            onSearch={() =>
                                                                                searchUser(
                                                                                    `guests.${index}`,
                                                                                    guest,
                                                                                    setFieldValue
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                disabled
                                                                            }
                                                                            isEditMode={
                                                                                isEditMode
                                                                            }
                                                                            isGuest={true}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            )
                                                        )}
                                                        {(!isEditMode ||
                                                            canEditRental) && (
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="flex-end"
                                                                sx={{ mt: 2 }}
                                                            >
                                                                <Button
                                                                    variant="outlined"
                                                                    onClick={() =>
                                                                        push({
                                                                            phone: {
                                                                                number: '',
                                                                                code: '1',
                                                                                country:
                                                                                    'US'
                                                                            },
                                                                            first_name:
                                                                                '',
                                                                            last_name: '',
                                                                            email: '',
                                                                            minor: false,
                                                                            renterFound:
                                                                                undefined
                                                                        })
                                                                    }
                                                                    disabled={loading}
                                                                >
                                                                    Add Guest
                                                                </Button>
                                                            </Stack>
                                                        )}
                                                    </>
                                                )}
                                            </FieldArray>
                                        </Grid>
                                    </Grid>
                                </div>
                                <div
                                    style={{
                                        flex: '0 0 auto',
                                        position: 'sticky',
                                        bottom: 0,
                                        paddingTop: 8,
                                        background: '#fff',
                                        zIndex: 1,
                                        borderTop: `1px solid ${theme.palette.divider}`
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                        spacing={2}
                                    >
                                        {(!isEditMode || canEditRental) && (
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={loading}
                                            >
                                                {isEditMode
                                                    ? 'Update Reservation'
                                                    : 'Create and Send Invite'}
                                            </Button>
                                        )}
                                        <Button
                                            variant="outlined"
                                            sx={{
                                                color: 'text.primary',
                                                borderColor: 'grey.300',
                                                textTransform: 'none'
                                            }}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                    </Stack>
                                </div>
                            </LocalizationProvider>
                        </form>
                    );
                }}
            </Formik>
        </>
    );
};

const mapStateToProps = ({ user, properties }) => {
    const { userData, activeOrg } = user;
    const { orgProperties } = properties;

    return {
        userData,
        activeOrg,
        orgProperties
    };
};

export default connect(mapStateToProps, {})(RentalForm);
